/**
 * Toast Notifications Container
 * Apple-style notification toasts
 */

import React from 'react';
import { useNotifications } from '../../context/NotificationContext';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles = {
  success: 'bg-white text-green-700',
  error: 'bg-white text-red-700',
  warning: 'bg-white text-yellow-700',
  info: 'bg-white text-primary-700',
};

const iconStyles = {
  success: 'text-green-500',
  error: 'text-red-500',
  warning: 'text-yellow-500',
  info: 'text-primary-500',
};

function ToastContainer() {
  const { toasts, dismissToast } = useNotifications();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => {
        const Icon = icons[toast.type] || icons.info;
        const style = styles[toast.type] || styles.info;
        const iconStyle = iconStyles[toast.type] || iconStyles.info;

        return (
          <div
            key={toast.id}
            className={`flex items-center p-4 rounded-apple-lg max-w-sm animate-slide-in ${style}`}
            style={{
              boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <Icon className={`h-5 w-5 flex-shrink-0 ${iconStyle}`} />
            <p className="ml-3 flex-1 text-sm font-normal">{toast.message}</p>
            <button
              onClick={() => dismissToast(toast.id)}
              className="ml-3 flex-shrink-0 text-navy-400 hover:text-navy-700 transition-colors duration-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateY(16px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
        }
      `}</style>
    </div>
  );
}

export default ToastContainer;
